// import 'core-js'
// import 'react-app-polyfill/stable'
// import { createRoot } from 'react-dom/client'
// import { Provider } from 'react-redux'
// import App from './App'
// import store from './store'
// const container = document.getElementById('root')

// const root = createRoot(container) // createRoot(container!) if you use TypeScript
// root.render(
//   <Provider store={store}>
//     <ThemeProvider>
//       <App />
//     </ThemeProvider>
//   </Provider>,
// )

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals

// NOTE: below code was added when switched to vite from CRA

// No-op client deploy trigger: 2026-04-25 (4).

// CRITICAL: Import React first to ensure it's initialized before Emotion/MUI
// This prevents "Cannot access uninitialized variable" errors in production builds
// The order matters: React must be imported before any components that use Emotion
import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import { Provider } from 'react-redux'
import { ThemeProvider } from './context/ThemeContext'
import store from './store'
import { loadCSSForRoute } from './utils/cssLoader'

// CRITICAL: Force React module initialization by accessing useInsertionEffect
// This ensures React is fully loaded before any Emotion code tries to access it
// This prevents "Cannot access uninitialized variable" errors in production builds
// when chunks load asynchronously and Emotion chunk might execute before React chunk
// This is a safeguard - accessing the property ensures the module is fully initialized
const _reactInitialized = React && typeof React.useInsertionEffect !== 'undefined' ? React.useInsertionEffect : null

// CRITICAL: Ensure ReactDOM is fully initialized before react-redux tries to access it
// This prevents "Cannot read properties of undefined (reading 'unstable_batchedUpdates')" errors
// React 18 doesn't have unstable_batchedUpdates, but react-redux should handle this gracefully
// However, we need to ensure ReactDOM is loaded before react-redux executes
// Accessing ReactDOM.createRoot ensures the module is fully initialized
if (typeof ReactDOM === 'undefined' || typeof ReactDOM.createRoot === 'undefined') {
  throw new Error('ReactDOM is not properly initialized')
}

// Phase 14ab — stale-chunk recovery. The classic SPA-cache problem:
//   1. User loads index.html with chunk references baked in (chunk-A.js, chunk-B.js).
//   2. Operator ships a new build; chunk-A.js / chunk-B.js are replaced with chunk-C.js
//      / chunk-D.js. Old chunks are removed from S3/CloudFront.
//   3. User stays on the page, navigates somewhere that triggers a lazy import.
//   4. Lazy import tries to fetch chunk-A.js → 404 → import() rejects with
//      ChunkLoadError → React.lazy renders undefined → React error #300 → blank page.
//
// The cure: on a chunk-load failure, force-reload the page so the browser fetches the
// fresh index.html with current chunk references. We guard with sessionStorage so we
// never enter an infinite reload loop if the new build is genuinely broken.
function isChunkLoadFailure(message) {
  if (!message) return false
  const lower = String(message).toLowerCase()
  return (
    lower.includes('chunkloaderror') ||
    lower.includes('loading chunk') ||
    lower.includes('failed to load module script') ||
    lower.includes('failed to fetch dynamically imported module') ||
    lower.includes('importing a module script failed') ||
    lower.includes('error loading dynamically imported module') ||
    lower.includes('strict mime type checking is enforced for module scripts') ||
    (lower.includes('net::err_aborted') && lower.includes('.js')) ||
    (lower.includes('404') && lower.includes('/js/'))
  )
}
function maybeReloadOnceForStaleChunk(message) {
  if (!isChunkLoadFailure(message)) return
  const FLAG = 'aumhumChunkReloadAttemptedAt'
  try {
    const last = Number(sessionStorage.getItem(FLAG) || '0')
    // Only attempt once every 30s — if a hard-reload didn't fix it, the new build is
    // genuinely broken and we shouldn't keep cycling through it.
    if (Date.now() - last < 30000) return
    sessionStorage.setItem(FLAG, String(Date.now()))
  } catch {
    /* sessionStorage unavailable in some embeds; reload anyway */
  }
  // hard reload — bypass HTTP cache so we get fresh index.html chunk hashes.
  if (typeof window !== 'undefined' && typeof window.location !== 'undefined') {
    window.location.reload()
  }
}
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    maybeReloadOnceForStaleChunk(event?.message || event?.error?.message)
  })
  window.addEventListener('unhandledrejection', (event) => {
    maybeReloadOnceForStaleChunk(event?.reason?.message || event?.reason)
  })
}

// import 'core-js'
// import 'react-app-polyfill/stable'

// Hide initial loader once React has rendered AND app data is loaded
// Use multiple strategies to ensure it works
function hideInitialLoader() {
  const loader = document.getElementById('initial-loader')
  if (loader) {
    loader.classList.add('hidden')
    if (document.body) {
      document.body.classList.add('react-loaded')
    }
  }
}

function InitialLoaderDismissSignal() {
  React.useEffect(() => {
    let animationFrameId = 0
    let nestedAnimationFrameId = 0

    animationFrameId = window.requestAnimationFrame(() => {
      nestedAnimationFrameId = window.requestAnimationFrame(() => {
        hideInitialLoader()
      })
    })

    return () => {
      window.cancelAnimationFrame(animationFrameId)
      window.cancelAnimationFrame(nestedAnimationFrameId)
    }
  }, [])

  return null
}

async function bootstrapApp() {
  try {
    await loadCSSForRoute(window.location.pathname || '/')
  } catch (error) {
    console.warn('[main] Failed to preload route-specific CSS:', error)
  }

  const root = ReactDOM.createRoot(document.getElementById('root'))
  root.render(
    // <React.StrictMode>
      <Provider store={store}>
        <ThemeProvider>
          <InitialLoaderDismissSignal />
          <div className="Root">
            <App />
          </div>
        </ThemeProvider>
      </Provider>
    // </React.StrictMode>,
  )
}

bootstrapApp()
